import React from "react";
import {
  Box, Container, Heading, Text, SimpleGrid, VStack, HStack,
  Button, Icon, Badge, Flex,
} from "@chakra-ui/react";
import { FaMapMarkerAlt, FaArrowRight, FaHome, FaChartLine, FaHandshake, FaShieldAlt } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../../components/SEOHead";
import { allCities } from "./propertyManagementData";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";

const SERVICES = [
  {
    icon: FaHome,
    title: "Tenant Acquisition",
    desc: "Professional photography, multi-platform advertising, rigorous applicant screening, and thorough reference checks — we find the right tenant, not just any tenant.",
  },
  {
    icon: FaShieldAlt,
    title: "Lease & Compliance",
    desc: "RTA-compliant leases, bond lodgement, condition reports, and ongoing legislative compliance — every property managed to Queensland's current tenancy laws.",
  },
  {
    icon: FaChartLine,
    title: "Rent Reviews & Renewals",
    desc: "Proactive rent reviews aligned to current market conditions, timely lease renewals, and vacancy minimisation strategies that protect your rental income.",
  },
  {
    icon: FaHandshake,
    title: "Maintenance Coordination",
    desc: "Trusted trade networks across Queensland, transparent job authorisation, owner-approved spend limits, and routine inspections that catch issues before they become costly.",
  },
];

const PropertyManagement: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/management"
        fallbackTitle="Property Management Queensland — One Property Australia"
        fallbackDescription="Professional property management across regional and metropolitan Queensland. Serving Hervey Bay, Rockhampton, Mackay, Townsville, Ipswich, Toowoomba, Brisbane, Sunshine Coast, Gold Coast, Gympie, and Gladstone."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 28, 73, 0.80)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={8} textAlign="center">
            <Badge
              bg={PROPERTY_PRIMARY}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              One Property Australia — Property Management
            </Badge>
            <Heading size="3xl" fontWeight="black" maxW="4xl" lineHeight="1.15">
              Queensland Property Management Done Right
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="3xl" lineHeight="1.8">
              Professional property management across metropolitan and regional Queensland. From Townsville to the Gold Coast — we protect your investment and maximise your returns.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/property/contact"
                size="lg"
                bg={PROPERTY_PRIMARY}
                color="white"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#0a6459" }}
                px={8}
                transition="all 0.2s"
              >
                Get a Free Rental Appraisal
              </Button>
              <Button
                as={RouterLink}
                to="/property/consultation"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.100" }}
                px={8}
                transition="all 0.2s"
              >
                Book a Consultation
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Stats Banner */}
      <Box bg={NAVY} py={10}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {[
              { value: "12", label: "Queensland Cities" },
              { value: "98%", label: "Avg Occupancy Rate" },
              { value: "3 Days", label: "Avg Days to Let" },
              { value: "24/7", label: "Owner & Tenant Support" },
            ].map((s) => (
              <VStack key={s.label} spacing={1} textAlign="center">
                <Heading size="2xl" color="white">{s.value}</Heading>
                <Text color="gray.400" fontSize="sm">{s.label}</Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* What We Do */}
      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading size="xl" color="gray.800">Full-Service Property Management</Heading>
            <Text color="gray.600" fontSize="lg" maxW="2xl">
              Everything your investment property needs, handled professionally — so you don't have to think about it.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {SERVICES.map((s) => (
              <Box key={s.title} bg="white" p={7} borderRadius="xl" border="1px solid" borderColor="gray.200" _hover={{ shadow: "lg", borderColor: PROPERTY_PRIMARY }} transition="all 0.2s">
                <Icon as={s.icon} boxSize={8} color={PROPERTY_PRIMARY} mb={4} />
                <Heading size="sm" color={NAVY} mb={3}>{s.title}</Heading>
                <Text fontSize="sm" color="gray.600" lineHeight="tall">{s.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* City Grid */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={12} textAlign="center">
          <Heading size="xl" color="gray.800">Serving Queensland — City by City</Heading>
          <Text color="gray.600" fontSize="lg" maxW="2xl">
            Click your city for local market insights, suburb coverage, and what makes property management in your area unique.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
          {allCities.map((city) => (
            <Box
              key={city.slug}
              as={RouterLink}
              to={`/property/management/${city.slug}`}
              borderRadius="xl"
              overflow="hidden"
              border="1px solid"
              borderColor="gray.200"
              _hover={{ transform: "translateY(-4px)", shadow: "2xl", borderColor: PROPERTY_PRIMARY, textDecoration: "none" }}
              transition="all 0.25s"
              bg="white"
              display="block"
            >
              {/* City card image */}
              <Box h="160px" overflow="hidden" position="relative">
                <Box
                  as="img"
                  src={city.heroImage}
                  alt={`Property management in ${city.name}, Queensland`}
                  w="full"
                  h="full"
                  objectFit="cover"
                />
                <Box
                  position="absolute"
                  top={0} left={0} right={0} bottom={0}
                  bgGradient={`linear(to-b, transparent 30%, ${NAVY}CC)`}
                />
                <Box position="absolute" bottom={3} left={4}>
                  <Text fontSize="xs" fontWeight="700" color="whiteAlpha.700" textTransform="uppercase" letterSpacing="wider">{city.region}</Text>
                </Box>
              </Box>

              <Box p={6}>
                <HStack mb={2}>
                  <Icon as={FaMapMarkerAlt} color={PROPERTY_PRIMARY} boxSize={4} />
                  <Heading size="md" color={NAVY}>{city.name}</Heading>
                </HStack>
                <Text fontSize="sm" color="gray.600" mb={4} lineHeight="tall" noOfLines={2}>
                  {city.tagline}
                </Text>
                <SimpleGrid columns={2} spacing={3}>
                  <Box bg="gray.50" p={3} borderRadius="lg">
                    <Text fontSize="xs" color="gray.500" mb={0.5}>Avg Yield</Text>
                    <Text fontWeight="700" color={PROPERTY_PRIMARY} fontSize="sm">{city.marketStats.avgRentalYield}</Text>
                  </Box>
                  <Box bg="gray.50" p={3} borderRadius="lg">
                    <Text fontSize="xs" color="gray.500" mb={0.5}>Vacancy Rate</Text>
                    <Text fontWeight="700" color={NAVY} fontSize="sm">{city.marketStats.vacancyRate}</Text>
                  </Box>
                </SimpleGrid>
                <Flex justify="flex-end" mt={4}>
                  <Text fontSize="sm" fontWeight="600" color={PROPERTY_PRIMARY}>
                    View {city.name} →
                  </Text>
                </Flex>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Why One Property */}
      <Box bg={NAVY} color="white" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={6}>
              <Heading size="xl">Why Landlords Choose One Property Australia</Heading>
              <Text color="gray.300" fontSize="lg" lineHeight="1.8">
                We're not a franchise or a national call centre. We're a Queensland-focused property investment and management firm — built by investors, for investors.
              </Text>
              <VStack align="flex-start" spacing={4}>
                {[
                  "Local market expertise across all 12 cities",
                  "Proactive rent reviews — not reactive",
                  "Transparent fee structure with no hidden charges",
                  "Dedicated property manager — not a rotating team",
                  "RTA-compliant processes, every time",
                  "Integrated investment strategy available on request",
                ].map((point) => (
                  <HStack key={point} spacing={3}>
                    <Box w={2} h={2} borderRadius="full" bg={PROPERTY_PRIMARY} flexShrink={0} mt={1} />
                    <Text color="gray.300" fontSize="sm">{point}</Text>
                  </HStack>
                ))}
              </VStack>
              <Button
                as={RouterLink}
                to="/property/contact"
                bg={PROPERTY_PRIMARY}
                color="white"
                size="lg"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#0a6459" }}
                transition="all 0.2s"
              >
                Speak to Our Team
              </Button>
            </VStack>
            <SimpleGrid columns={2} spacing={4}>
              {[
                { label: "No Lock-In Contracts", desc: "We earn your business every month — not through fine print." },
                { label: "Investor-First Advice", desc: "Our property managers are also investors. We think like owners." },
                { label: "Fast Maintenance Response", desc: "Trusted trade networks in every city we operate in." },
                { label: "Owner Portal Access", desc: "Real-time visibility of statements, inspections, and maintenance." },
              ].map((item) => (
                <Box key={item.label} bg="whiteAlpha.100" p={5} borderRadius="xl">
                  <Text fontWeight="700" color="white" mb={2} fontSize="sm">{item.label}</Text>
                  <Text fontSize="xs" color="gray.400">{item.desc}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl" color={NAVY}>Ready to Switch Property Managers?</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Switching is easier than you think — we handle the transition from your current manager at no cost. Get a free rental appraisal and see what your property should be earning.
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/property/contact"
                size="lg"
                bg={PROPERTY_PRIMARY}
                color="white"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#0a6459" }}
                transition="all 0.2s"
              >
                Get a Free Rental Appraisal
              </Button>
              <Button
                as={RouterLink}
                to="/property/faq"
                size="lg"
                variant="outline"
                borderColor={PROPERTY_PRIMARY}
                color={PROPERTY_PRIMARY}
                _hover={{ bg: PROPERTY_PRIMARY, color: "white" }}
                transition="all 0.2s"
              >
                Read Our FAQ
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyManagement;
